import { type ComponentType } from 'react';
import {
  flexRender,
  type Row,
  RowData,
  Table as TanstackTable,
} from '@tanstack/react-table';

import { Pagination } from '@/components/table/pagination';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';

declare module '@tanstack/react-table' {
  interface TableMeta<TData extends RowData> {
    updateData?: (
      rowId: string,
      columnId: Extract<keyof TData, string>,
      value: unknown
    ) => void;
    selectedCell?: { rowId: string; columnId: string } | null;
    setSelectedCell?: (
      cell: { rowId: string; columnId: string } | null
    ) => void;
    isRowLocked?: (row: TData) => boolean;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- generics mirror TanStack `ColumnMeta` for declaration merge
  interface ColumnMeta<TData extends RowData, TValue> {
    inputType?: 'text' | 'number' | 'datetime-local';
    readOnly?: boolean;
  }
}

interface DataTableProps<TData> {
  table: TanstackTable<TData>;
  empty?: string;
  isNested?: boolean;
  expansion?: ComponentType<{ row: Row<TData> }>;
  onRowClick?: (row: Row<TData>) => void;
  className?: string;
}

export const DataTable = <TData,>({
  className,
  table,
  empty,
  isNested = false,
  // eslint-disable-next-line @typescript-eslint/naming-convention
  expansion: Expansion,
  onRowClick,
}: DataTableProps<TData>) => {
  const columns = table.getAllColumns();

  return (
    <div className={cn('flex flex-col gap-4', className)}>
      <div
        className={cn({
          'rounded-md border': !isNested,
        })}>
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.length ? (
              table.getRowModel().rows.map((row) => (
                <>
                  <TableRow
                    key={row.id}
                    data-state={row.getIsSelected() && 'selected'}
                    data-clickable={typeof onRowClick === 'function'}
                    className={cn({
                      'cursor-pointer': typeof onRowClick === 'function',
                    })}
                    onClick={() => onRowClick?.(row)}>
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                  {row.getIsExpanded() && Expansion && (
                    <TableRow>
                      <td colSpan={columns.length}>
                        <Expansion row={row} />
                      </td>
                    </TableRow>
                  )}
                </>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className='h-24 text-center'>
                  {empty ?? 'No results'}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      {!isNested && <Pagination table={table} />}
    </div>
  );
};
